import { all, put, select, takeEvery } from '@redux-saga/core/effects';
import { AnalyticsTarget } from 'enums/AnalyticsTarget';
import { InputWidgetElementType } from 'enums/InputWidgetElementType';
import { MessageCommandType } from 'enums/MessageCommandType';
import { MessageType } from 'enums/MessageType';
import { UserPermission } from 'enums/UserPermission';
import { UserPermissionState } from 'enums/UserPermissionState';
import { WidgetFeature } from 'enums/WidgetFeature';
import { DynamicInputWidgetElementData } from 'interfaces/DynamicInputWidgetElementData';
import { InputWidgetElementData } from 'interfaces/InputWidgetElementData';
import {
    DeviceFingerprintResponseCommandData,
    GeoLocationResponseCommandData,
    InputWidgetCommandData,
    SurveyRequestCommandData,
    UpdateParametersCommandData,
    UserPermissionsRequestCommandData,
    UserPermissionsResponseCommandData,
} from 'interfaces/MessageData';
import { TimeSlotDateItemData } from 'interfaces/TimeSlotElementData';
import { WidgetParameters } from 'interfaces/WidgetParameters';
import { DefaultRootState } from 'react-redux';
import { call } from 'redux-saga/effects';
import { services } from 'services/container';
import { showToast } from 'state/chat/chat.actions';
import { setFeaturesActive } from 'state/features/features.actions';
import { selectFeatureEnabled } from 'state/features/features.selectors';
import { sendMessage } from 'state/history/history.actions';
import { receiveInputWidget, receiveSurveyWidget } from 'state/inputWidget/inputWidget.actions';
import { updateParameters } from 'state/parameters/parameters.actions';
import { createReachGoalActionBy } from 'utils/analytics';
import { getIsIOS, isDefinedNavigator } from 'utils/native';
import { getUserPermissionState } from 'utils/permissions';

import { openGameWidget } from '../game/game.actions';
import {
    CommandActionType,
    ReceiveCommandAction,
    sendCommand,
    SendCommandAction,
} from './commands.actions';

const i8lDelimiter = '.';
const datesDelimiter = ';';

function* sendCommandSaga() {
    yield takeEvery(CommandActionType.Send, function* (action: SendCommandAction) {
        const command = action.payload;
        const text = command.type;

        yield put(
            sendMessage({
                type: MessageType.Tech,
                text,
                metadata: { command },
            }),
        );
    });
}

function* receiveCommandSaga() {
    yield takeEvery(CommandActionType.Receive, function* (action: ReceiveCommandAction) {
        try {
            const command = action.payload;

            switch (command.type) {
                case MessageCommandType.WaitingGame: {
                    const gameEnabled = yield select((state: DefaultRootState) =>
                        selectFeatureEnabled(state, WidgetFeature.WaitingGame),
                    );

                    if (gameEnabled) {
                        yield put(openGameWidget({ isInitiatedByDeepLink: false }));
                    }

                    break;
                }

                case MessageCommandType.InputWidget: {
                    const inputWidgetData = (command as InputWidgetCommandData).text_chat_widget;

                    const elements = [] as InputWidgetElementData[];
                    const dynamicElements = [] as DynamicInputWidgetElementData[];

                    const withProperties = (element): InputWidgetElementData => ({
                        ...element,
                        properties: inputWidgetData.properties,
                    });

                    for (const element of inputWidgetData.elements) {
                        if ((element.type as string) === 'dynamic') {
                            const dynamicElement = element as DynamicInputWidgetElementData;

                            const { options, parentId, elementId } = dynamicElement;

                            if (!options || !parentId) return;

                            const parentElement = inputWidgetData.elements.find(
                                (el) => el.elementId === parentId,
                            );

                            if (!parentElement) continue;

                            const defaultOption = options['else'] || Object.values(options)[0];

                            if (!defaultOption) continue;

                            defaultOption.dynamicElementId = elementId;
                            dynamicElements.push(dynamicElement);
                            elements.push(withProperties(defaultOption));
                        } else {
                            elements.push(withProperties(element));
                        }
                    }

                    inputWidgetData.elements = elements;
                    inputWidgetData.dynamicElements = dynamicElements;
                    const platformDelimiter = getIsIOS() ? '/' : '.';

                    inputWidgetData.elements.forEach((element) => {
                        switch (element.type) {
                            case InputWidgetElementType.DateSelector:
                                const value = element.value as string;

                                if (!!element?.isPeriod) {
                                    const adjustValue = (value: string) =>
                                        String(parseInt(value) + 2000);
                                    const splitValue = value
                                        .replace(datesDelimiter, i8lDelimiter)
                                        .split(i8lDelimiter);

                                    splitValue[2] = adjustValue(splitValue[2]);
                                    splitValue[5] = adjustValue(splitValue[5]);
                                    splitValue.splice(3, 0, datesDelimiter);

                                    element.value = splitValue
                                        .join(i8lDelimiter)
                                        .replace(
                                            [i8lDelimiter, datesDelimiter, i8lDelimiter].join(''),
                                            datesDelimiter,
                                        );
                                } else {
                                    element.value = (() => {
                                        const [dd, mm, yy] = value.split(i8lDelimiter);

                                        return [dd, mm, parseInt(yy) + 2000].join(
                                            platformDelimiter,
                                        );
                                    })();
                                }

                                break;

                            case InputWidgetElementType.TimeSlot:
                                element.items.forEach((item: TimeSlotDateItemData) => {
                                    if (!item) return;

                                    const [day, month, year] = item.value.split(i8lDelimiter);

                                    const newDateParts = [day, month, parseInt(year) + 2000];
                                    const newDate = newDateParts.join(platformDelimiter);

                                    if (new Date(newDate).toString() !== 'Invalid Date') {
                                        item.value = newDateParts.join(i8lDelimiter);
                                    }
                                });
                                break;
                        }
                    });

                    yield put(receiveInputWidget(inputWidgetData));

                    if (inputWidgetData.analytics) {
                        services.analytics.sendScreenViewEvent(inputWidgetData.analytics);
                    }

                    break;
                }

                case MessageCommandType.UserPermissionsRequest: {
                    const { permissions } = (command as UserPermissionsRequestCommandData).data;
                    const result = [] as { name: UserPermission; status: UserPermissionState }[];

                    const unsafePermissions = !isDefinedNavigator(['permissions']);
                    const requestedGeolocation =
                        permissions.length === 1 && permissions[0] === 'geolocation';

                    if (unsafePermissions && requestedGeolocation) {
                        // exception for hybrid
                        result.push({
                            name: UserPermission.Geolocation,
                            status: UserPermissionState.Granted,
                        });
                    } else if (!unsafePermissions) {
                        for (const name of permissions) {
                            const status: UserPermissionState = yield call(
                                getUserPermissionState,
                                name,
                            );
                            result.push({ name, status });
                        }
                    }

                    yield put(
                        sendCommand({
                            type: MessageCommandType.UserPermissionsResponse,
                            data: {
                                permissions: result,
                            },
                        } as UserPermissionsResponseCommandData),
                    );

                    break;
                }

                case MessageCommandType.UpdateParameters: {
                    const { parameters } = (command as UpdateParametersCommandData).data;
                    if (!parameters?.channel && !parameters?.overrideFeatures) return;

                    const { channel, overrideFeatures } = parameters;

                    if (overrideFeatures) {
                        yield put(setFeaturesActive(overrideFeatures));
                    }

                    if (channel) {
                        const currentParameters: WidgetParameters = yield select(
                            (state: DefaultRootState) => state.parameters,
                        );

                        yield put(
                            updateParameters({
                                ...currentParameters,
                                channel,
                            }),
                        );
                    }

                    break;
                }

                case MessageCommandType.SendFingerPrintRequest: {
                    const state: DefaultRootState = yield select();
                    const fingerPrintEnabled = selectFeatureEnabled(
                        state,
                        WidgetFeature.SendFingerPrint,
                    );

                    if (fingerPrintEnabled) {
                        const cmd: DeviceFingerprintResponseCommandData = {
                            type: MessageCommandType.SendFingerPrintResponse,
                            data: {
                                value: state?.parameters?.deviceFingerprint ?? '',
                            },
                        };

                        yield put(sendCommand(cmd));
                    }

                    break;
                }

                case MessageCommandType.GeoLocationRequest: {
                    const [permission, coords]: [UserPermissionState, GeolocationCoordinates] =
                        yield call(() =>
                            new Promise<[UserPermissionState, GeolocationCoordinates]>(
                                (resolve) => {
                                    const unsafeGeolocation = !isDefinedNavigator(['geolocation']);

                                    if (unsafeGeolocation) {
                                        const permission = UserPermissionState.Denied;
                                        resolve([permission, null]);
                                        return;
                                    }

                                    navigator.geolocation.getCurrentPosition(
                                        (pos) => {
                                            const permission = UserPermissionState.Granted;
                                            const coords = pos.coords;

                                            resolve([permission, coords]);
                                        },
                                        async () => {
                                            const unsafePermissions = !isDefinedNavigator([
                                                'permissions',
                                            ]);
                                            let permission: UserPermissionState = null;

                                            if (unsafePermissions) {
                                                permission = UserPermissionState.Granted;
                                            } else {
                                                permission = await getUserPermissionState(
                                                    UserPermission.Geolocation,
                                                );
                                            }

                                            resolve([permission, null]);
                                        },
                                    );
                                },
                            ).catch((err) => {
                                services.log.error(`err in geo: ${err}`);
                            }),
                        );

                    const cmd: GeoLocationResponseCommandData = {
                        type: MessageCommandType.GeoLocationResponse,
                        data: {
                            permission,
                        },
                    };

                    if (coords) {
                        cmd.data.location = {
                            latitude: coords.latitude.toString(),
                            longitude: coords.longitude.toString(),
                        };
                    }

                    yield put(sendCommand(cmd));

                    if (permission !== UserPermissionState.Granted || !coords) {
                        yield put(
                            showToast({
                                message:
                                    'Невозможно определить ваше местоположение. Разрешите в настройках геолокацию',
                                type: 'info',
                            }),
                        );
                    }

                    break;
                }

                case MessageCommandType.SurveyRequest: {
                    const surveyRequestData = command as unknown as SurveyRequestCommandData;

                    services.analytics.send(
                        createReachGoalActionBy(
                            AnalyticsTarget.SHOW_SURVEY_WIDGET,
                            surveyRequestData.survey.type,
                        ),
                    );

                    yield put(receiveSurveyWidget(surveyRequestData));
                }
            }
        } catch (e) {
            services.log.error(`Failed to process command '${action.payload?.type}': ${e}`);
        }
    });
}

export function* commandsSaga() {
    yield all([sendCommandSaga(), receiveCommandSaga()]);
}
